/**
 * API client for project list / OST persistence endpoints.
 *
 * Request plumbing (session cookie, JSON parsing, error shape) lives in
 * httpClient.ts — see that module for the error convention.
 */
import { getJson, postJson, putJson, patchJson, deleteJson, ErrorResponse } from './httpClient';

export async function fetchProjects(): Promise<unknown | ErrorResponse> {
    return getJson('/api/projects');
}

export async function createProject(name: string): Promise<unknown | ErrorResponse> {
    return postJson('/api/projects', { name });
}

export async function getProject(id: string): Promise<unknown | ErrorResponse> {
    return getJson(`/api/projects/${id}`);
}

export async function saveProjectOst(id: string, ost: unknown, insights?: unknown): Promise<unknown | ErrorResponse> {
    return putJson(`/api/projects/${id}/ost`, { ost, insights });
}

export async function deleteProject(id: string): Promise<unknown | ErrorResponse> {
    return deleteJson(`/api/projects/${id}`);
}

// New OST-scoped APIs
export async function listOsts(projectId: string): Promise<unknown | ErrorResponse> {
    return getJson(`/api/projects/${projectId}/osts`);
}

export async function createOst(projectId: string, name: string, ost?: unknown): Promise<unknown | ErrorResponse> {
    return postJson(`/api/projects/${projectId}/osts`, { name, ost });
}

export async function getOst(projectId: string, ostId: string): Promise<unknown | ErrorResponse> {
    return getJson(`/api/projects/${projectId}/osts/${ostId}`);
}

export async function saveOst(projectId: string, ostId: string, ost: unknown, insights?: unknown): Promise<unknown | ErrorResponse> {
    return putJson(`/api/projects/${projectId}/osts/${ostId}`, { ost, insights });
}

export async function deleteOst(projectId: string, ostId: string): Promise<unknown | ErrorResponse> {
    return deleteJson(`/api/projects/${projectId}/osts/${ostId}`);
}

/**
 * Persists the project's custom AI instructions (used to tailor insights-summary and
 * opportunity-suggestion generation). The server validates the text with the same
 * guardrail applied at generation time, so an invalid prompt is rejected here too.
 */
export async function saveCustomAiInstructions(
    projectId: string,
    customInstructions: string,
): Promise<{ success?: boolean; customAiInstructions?: string; error?: string }> {
    return patchJson<{ success?: boolean; customAiInstructions?: string }>(
        `/api/projects/${projectId}/custom-ai-instructions`,
        { customInstructions },
    );
}

/**
 * Persists the Plan page's card placements/order/lines/axisLabels/zoom/connections/enablerCards
 * (shared across every OST tree in the project — see PlanPage.tsx and PlanBoardState in types.ts).
 */
export async function savePlanBoard(
    projectId: string,
    placements: Record<string, unknown>,
    order: string[],
    lines: unknown[] = [],
    axisLabels: unknown[] = [],
    zoom?: number,
    connections: unknown[] = [],
    enablerCards: unknown[] = [],
): Promise<unknown | ErrorResponse> {
    return putJson(`/api/projects/${projectId}/plan-board`, { placements, order, lines, axisLabels, zoom, connections, enablerCards });
}

export async function addSuggestedOpportunities(
    projectId: string,
    ostId: string | null,
    opportunities: Array<{ tempId: string; title: string; description: string; parentTempId: string | null }>,
): Promise<unknown | ErrorResponse> {
    return postJson(`/api/projects/${projectId}/add-suggested-opportunities`, { ostId, opportunities });
}
